/**
 * EditProductPage Component - Story 6.4: Create Edit Product Form
 * Page for editing existing product listings
 */

import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { SellerDashboardLayout } from '../components/SellerDashboardLayout';
import { ProductForm } from '../components/ProductForm/ProductForm';
import { LoadingSpinner } from '@/components/ui';
import { Button } from '@/components/ui/button';
import { AlertCircle, ArrowLeft } from 'lucide-react';
import { microservicesClient } from '@/services/microservices/index';
import { getProductsQuantities } from '../services/inventoryBridgeService';
import { salesService } from '@/modules/sales/services/salesService';
import './EditProductPage.css';

/**
 * Props interface for EditProductPage component
 */
export interface EditProductPageProps {
  /** Optional callback when a product is successfully updated */
  onProductUpdated?: (productId: string) => void;
  /** When true, renders without SellerDashboardLayout wrapper (for embedding in other layouts) */
  disableLayout?: boolean;
}

/**
 * Product data retrieved from API for pre-filling form
 * Backend sends ProductDetailResponse with 'productName' instead of 'name'
 */
interface ProductData {
  id: string;
  name?: string; // Frontend field
  productName?: string; // Backend field
  category: string;
  description: string;
  price: number;
  unit: string;
  quantity?: number;
  quantityAvailable?: number; // Backend field
  minimumOrderQuantity?: number;
  state?: string;
  district?: string;
  pincode?: string;
  address?: string;
  status: string;
  images: Array<{
    imageId: string;
    url: string;
  }> | string[];
}

/**
 * EditProductPage Component
 * Renders the edit product form within the seller dashboard layout
 * Loads existing product data and pre-fills the form
 *
 * @component
 * @example
 * <EditProductPage onProductUpdated={(id) => console.log('Updated:', id)} />
 */
export const EditProductPage: React.FC<EditProductPageProps> = ({
  onProductUpdated,
  disableLayout = false,
}) => {
  const { productId } = useParams<{ productId: string }>();
  const navigate = useNavigate();
  const [product, setProduct] = useState<ProductData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [realAvailableQuantity, setRealAvailableQuantity] = useState<number | null>(null);
  const [quantityLoading, setQuantityLoading] = useState(false);

  // Set page title and meta description on mount
  useEffect(() => {
    document.title = 'Edit Product | SAUBHAGYA Seller Dashboard';

    // Set meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute(
        'content',
        'Edit your product listing on the SAUBHAGYA marketplace'
      );
    }
  }, []);

  // Load product data from API
  useEffect(() => {
    if (!productId) {
      setError('Product ID is missing');
      setIsLoading(false);
      return;
    }

    const loadProduct = async () => {
      try {
        setIsLoading(true);
        setError(null);

        console.log('[EditProductPage] Loading product:', productId);

        const response = await microservicesClient.callService(
          'commerce-service',
          `/api/v1/marketplace/products/${productId}`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
            },
          }
        );

        if (!response.ok) {
          if (response.status === 404) {
            throw new Error('Product not found');
          }
          if (response.status === 403) {
            throw new Error('You do not have permission to edit this product');
          }
          throw new Error('Failed to load product');
        }

        const data = await response.json();

        // Backend returns ProductDetailResponse directly (not wrapped)
        // Handle both wrapped and unwrapped responses for backward compatibility
        const productData = data.data || data;

        if (!productData || !productData.id) {
          throw new Error('Invalid response structure');
        }

        console.log('[EditProductPage] Successfully loaded product:', {
          id: productData.id,
          name: productData.name || productData.productName,
        });

        setProduct(productData);
      } catch (err) {
        console.error('[EditProductPage] Error loading product:', err);
        const errorMessage =
          err instanceof Error ? err.message : 'Failed to load product';
        setError(errorMessage);
      } finally {
        setIsLoading(false);
      }
    };

    loadProduct();
  }, [productId]);

  // Fetch real available quantity from inventory service
  useEffect(() => {
    if (!product || !product.id) {
      setRealAvailableQuantity(null);
      setQuantityLoading(false);
      return;
    }

    const fetchQuantity = async () => {
      setQuantityLoading(true);
      try {
        // Convert product ID to string for consistency
        const productIdStr = String(product.id);
        console.log('[EditProductPage] Fetching real available quantity for product:', productIdStr);

        const quantityMap = await getProductsQuantities([productIdStr]);

        // Log what's in the map for debugging
        console.log('[EditProductPage] Quantity map size:', quantityMap.size);
        console.log('[EditProductPage] Quantity map keys:', Array.from(quantityMap.keys()));

        // Try to get with the product ID (should be in the map as string)
        const qty = quantityMap.get(productIdStr);

        if (qty && qty.availableQuantity > 0) {
          console.log('[EditProductPage] ✅ Successfully fetched available quantity from inventory:', qty.availableQuantity, qty.unit);
          setRealAvailableQuantity(qty.availableQuantity);
        } else {
          console.log('[EditProductPage] ⚠️ No quantity data from inventory API (0 or failed), trying fallback...');

          // Fallback 1: Try to fetch from salesService using product name
          const productName = product.name || product.productName;
          let fallbackAvailableQty: number | null = null;

          if (productName) {
            try {
              const fallbackQty = await salesService.getProductQuantities(productName);
              fallbackAvailableQty = fallbackQty.available !== undefined ? fallbackQty.available : fallbackQty.availableQuantity || 0;
              console.log('[EditProductPage] Fallback 1 (salesService) quantity:', fallbackAvailableQty);
            } catch (fallbackErr) {
              console.error('[EditProductPage] Fallback 1 (salesService) failed:', fallbackErr);
            }
          }

          // Fallback 2: If fallback 1 returned 0 or failed, use product API's quantityAvailable field
          if (!fallbackAvailableQty || fallbackAvailableQty === 0) {
            const apiQuantity = product.quantity || product.quantityAvailable || 0;
            if (apiQuantity > 0) {
              console.log('[EditProductPage] ✅ Using Fallback 2 (product API):', apiQuantity);
              setRealAvailableQuantity(apiQuantity);
            } else {
              console.log('[EditProductPage] ⚠️ All fallbacks returned 0');
              setRealAvailableQuantity(fallbackAvailableQty || 0);
            }
          } else {
            console.log('[EditProductPage] ✅ Using Fallback 1 (salesService):', fallbackAvailableQty);
            setRealAvailableQuantity(fallbackAvailableQty);
          }
        }
      } catch (err) {
        console.error('[EditProductPage] ❌ Error fetching available quantity:', err);
        setRealAvailableQuantity(null);
      } finally {
        setQuantityLoading(false);
      }
    };

    fetchQuantity();
  }, [product?.id, product?.name, product?.productName]);

  // Update form when real quantity is fetched
  // This ensures the form always shows the real quantity, not stale data
  useEffect(() => {
    if (realAvailableQuantity !== null) {
      console.log('[EditProductPage] 🔄 Updating form with real quantity:', realAvailableQuantity);
      // Note: We can't directly access form here as it's in ProductForm
      // The quantity will be used in initialData which gets picked up on re-render
    }
  }, [realAvailableQuantity]);

  /**
   * Handle successful product update
   * @param productId - The ID of the updated product
   */
  const handleProductUpdated = (productId: string) => {
    console.log('[EditProductPage] Product updated successfully:', productId);

    // Call optional callback if provided
    if (onProductUpdated) {
      onProductUpdated(productId);
    }

    // Navigation is handled by ProductForm component
  };

  /**
   * Handle back navigation
   */
  const handleBack = () => {
    // Detect if we're in sales or marketplace context
    if (window.location.pathname.includes('/sales')) {
      navigate('/sales/my-products');
    } else {
      navigate('/marketplace/seller/products');
    }
  };

  // Helper to wrap content with or without layout
  const wrapContent = (content: React.ReactNode, subtitle?: string) => {
    if (disableLayout) {
      return (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Edit Product</h2>
              {subtitle && <p className="text-gray-600 text-sm mt-1">{subtitle}</p>}
            </div>
            <Button
              variant="outline"
              onClick={handleBack}
              className="flex items-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Products
            </Button>
          </div>
          {content}
        </div>
      );
    }

    return (
      <SellerDashboardLayout
        activeNavItemId="my-products"
        breadcrumbs={[
          { label: 'Dashboard', path: '/marketplace/seller/dashboard' },
          { label: 'My Products', path: '/marketplace/seller/products' },
          { label: 'Edit Product' },
        ]}
        hideStats={true}
        pageTitle="Edit Product"
        pageSubtitle={subtitle}
      >
        {content}
      </SellerDashboardLayout>
    );
  };

  // Render loading state
  if (isLoading) {
    return wrapContent(
      <div className="edit-product-page-loading" data-testid="edit-product-loading">
        <div className="loading-container">
          <LoadingSpinner size="lg" />
          <p>Loading product details...</p>
        </div>
      </div>
    );
  }

  // Render error state
  if (error) {
    return wrapContent(
      <div className="edit-product-page-error" data-testid="edit-product-error">
        <div className="error-container">
          <AlertCircle className="error-icon" />
          <h2>Unable to Load Product</h2>
          <p>{error}</p>
          <button
            onClick={handleBack}
            className="error-action-button"
          >
            Back to My Products
          </button>
        </div>
      </div>
    );
  }

  // Render form with pre-filled data
  if (!product) {
    return wrapContent(
      <div className="edit-product-page-error" data-testid="edit-product-not-found">
        <div className="error-container">
          <AlertCircle className="error-icon" />
          <h2>Product Not Found</h2>
          <p>The product you are trying to edit could not be found.</p>
          <button
            onClick={handleBack}
            className="error-action-button"
          >
            Back to My Products
          </button>
        </div>
      </div>
    );
  }

  // Pass images as-is from the product data - ProductForm will handle the transformation
  // No transformation needed here since ProductForm expects the original backend structure
  const transformedImages = Array.isArray(product.images)
    ? product.images.map((img) => {
        // Handle both string URLs and objects with url/imageId properties
        if (typeof img === 'string') {
          // Convert string URL to the expected backend object format
          return {
            url: img,
            imageId: `string-image-${Math.random()}`,
          };
        } else if (img && typeof img === 'object' && (img.url || img.preview)) {
          // Already in object format, ensure it has url and imageId
          return {
            url: img.url || img.preview || '',
            imageId: img.imageId || img.id || `object-image-${Math.random()}`,
          };
        }
        return img;
      })
    : [];

  return wrapContent(
    <div className="edit-product-page" data-testid="edit-product-page">
      <ProductForm
        // Force re-mount when realAvailableQuantity is fetched so form re-initializes with new value
        key={`product-form-${product.id}-${realAvailableQuantity ?? 'loading'}`}
        mode="edit"
        productId={product.id}
        initialData={{
          name: product.name || product.productName,
          category: product.category,
          description: product.description,
          price: product.price,
          unit: product.unit,
          quantity: realAvailableQuantity !== null ? realAvailableQuantity : (product.quantity || product.quantityAvailable),
          minimumOrderQuantity: product.minimumOrderQuantity,
          state: product.state || '',
          district: product.district || '',
          pincode: product.pincode || '',
          address: product.address || '',
          images: transformedImages,
        }}
        onSuccess={handleProductUpdated}
      />
    </div>,
    `Update details for "${product.name || product.productName}" ${quantityLoading ? '(Loading quantity...)' : ''}`
  );
};

export default EditProductPage;
